import { useState } from 'react'
import { X } from 'lucide-react'

interface AdjustStockModalProps {
  currentQuantity: number
  onSave: (data: { adjustment: number; new_quantity: number; reason: string; adjusted_by: string }) => Promise<void>
  onClose: () => void
}

export function AdjustStockModal({ currentQuantity, onSave, onClose }: AdjustStockModalProps) {
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove'>('add')
  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState('')
  const [adjustedBy, setAdjustedBy] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const qty = parseInt(quantity)
    if (!qty || qty <= 0) {
      setError('A valid quantity is required')
      return
    }
    if (!reason.trim()) {
      setError('Reason is required')
      return
    }
    if (adjustmentType === 'remove' && qty > currentQuantity) {
      setError('Removal quantity exceeds available stock')
      return
    }

    const adjustment = adjustmentType === 'add' ? qty : -qty
    const newQuantity = currentQuantity + adjustment

    setSaving(true)
    await onSave({
      adjustment,
      new_quantity: newQuantity,
      reason: reason.trim(),
      adjusted_by: adjustedBy.trim(),
    })
    setSaving(false)
  }

  const labelClass = 'text-[11px] text-text-muted block mb-0.5'
  const inputClass = 'w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="adjust-stock-modal">
      <div
        className="bg-surface border border-border rounded-[8px] w-full max-w-sm"
        style={{ boxShadow: 'var(--shadow-elevation-2)' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-light">
          <h2 className="text-[14px] font-medium text-text-primary">Adjust Stock</h2>
          <button
            data-testid="adjust-stock-close"
            onClick={onClose}
            className="w-7 h-7 inline-flex items-center justify-center rounded-[4px] hover:bg-hover cursor-pointer"
          >
            <X size={14} className="text-text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-3 flex flex-col gap-3">
          <div>
            <label className={labelClass}>Adjustment Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="adjust-stock-add"
                onClick={() => setAdjustmentType('add')}
                className={`flex-1 h-7 rounded-[4px] text-[13px] border cursor-pointer transition-colors duration-100 ${
                  adjustmentType === 'add'
                    ? 'bg-accent text-white border-accent'
                    : 'bg-base text-text-primary border-border hover:bg-hover'
                }`}
              >
                Add (+)
              </button>
              <button
                type="button"
                data-testid="adjust-stock-remove"
                onClick={() => setAdjustmentType('remove')}
                className={`flex-1 h-7 rounded-[4px] text-[13px] border cursor-pointer transition-colors duration-100 ${
                  adjustmentType === 'remove'
                    ? 'bg-status-error text-white border-status-error'
                    : 'bg-base text-text-primary border-border hover:bg-hover'
                }`}
              >
                Remove (-)
              </button>
            </div>
          </div>

          <div>
            <label className={labelClass}>Quantity *</label>
            <input
              data-testid="adjust-stock-quantity"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className={inputClass}
              placeholder="Enter quantity"
            />
          </div>

          <div>
            <label className={labelClass}>Reason *</label>
            <input
              data-testid="adjust-stock-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={inputClass}
              placeholder="e.g., Shipment received"
            />
          </div>

          <div>
            <label className={labelClass}>Adjusted By</label>
            <input
              data-testid="adjust-stock-adjusted-by"
              value={adjustedBy}
              onChange={(e) => setAdjustedBy(e.target.value)}
              className={inputClass}
              placeholder="e.g., Dr. Sarah Chen"
            />
          </div>

          {error && (
            <p className="text-[12px] text-status-error" data-testid="adjust-stock-error">{error}</p>
          )}

          <div className="flex justify-end gap-2 mt-1">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="adjust-stock-save"
              disabled={saving}
              className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
